import { Directive, inject } from '@angular/core';
import {
  ContentSearcherService,
  PlayerHost,
  PlayerMix,
  PlayerProgram,
  StationManagerService,
} from '@splixer/player';
import { ContentMenuItem } from '../models';
import dayjs from 'dayjs';
import { nString } from '@hidat/huijs-common';
import { HostFilter, ProgramFilter, MixSearchFilter } from '@splixer/common';
import { map, Observable, of } from 'rxjs';

/**
 * Base content item manager and cache.
 * This is an attempt to consolidate all the ways different players load and manage this information.
 */
@Directive()
export class ContentItemCacheBase {
  protected contentSearcher = inject(ContentSearcherService);
  protected station = inject(StationManagerService);
  protected programsMinPriority = 100;
  protected hostsMinPriority = 100;
  protected mixesMinPriority = 100;

  protected lastLoadedAt: dayjs.Dayjs | undefined;
  protected discoveryHosts: ContentMenuItem[] = [];
  protected discoveryPrograms: ContentMenuItem[] = [];
  protected discoveryMixes: ContentMenuItem[] = [];
  protected dailyArchives = new Map<string, ContentMenuItem[]>();
  protected dailyArchivesUpdatedAt: dayjs.Dayjs | undefined;

  /**
   * Builds the cache from the associated items currently loaded in the station
   */
  public loadFromStation() {
    this.discoveryPrograms = this.programsToMenuItems(
      this.station.programs,
      { minPriority: this.programsMinPriority },
      false,
    );
    this.discoveryHosts = this.hostsToMenuItems(
      this.station.hosts,
      { minPriority: this.hostsMinPriority },
      false,
    );
    this.discoveryMixes = this.mixesToMenuItems(
      this.station.featuredMixes,
      'd',
      { minPriority: this.mixesMinPriority },
      false,
    );
    this.lastLoadedAt = dayjs();
  }

  /***** Content Menu Item Generators - Override in your derived classes as thses do not set routerLink or contentId *****/

  public fromProgram(p: PlayerProgram): ContentMenuItem | undefined {
    if (p.uid) {
      const mi: ContentMenuItem = {
        id: p.uid,
        name: p.name ?? '*** ERROR ***',
        title: p.branding?.keyword,
        tagLine: p.tagLine,
        backgroundImage:
          p.branding?.avatarImageUrl ?? p.imageUrl ?? p._host?.imageUrl,
        thumbnailImage:
          p.branding?.thumbnailImageUrl ?? p._host?.branding?.thumbnailImageUrl,
      };
      return mi;
    }
    return undefined;
  }

  public fromHost(host: PlayerHost): ContentMenuItem | undefined {
    if (host.uid) {
      const mi: ContentMenuItem = {
        id: host.uid,
        name: host.name ?? '*** ERROR ***',
        //title: host.branding.keyword,
        tagLine: host.tagLine,
        backgroundImage: host.imageUrl ?? this.station.defaultHostImageUrl,
        thumbnailImage: host.branding?.thumbnailImageUrl,
      };
      return mi;
    }

    return undefined;
  }

  public fromMix(
    mix: PlayerMix,
    thirdLine: 'h' | 'd' | undefined,
  ): ContentMenuItem | undefined {
    if (mix.uid) {
      const mi: ContentMenuItem = {
        id: mix.uid,
        name: mix.name ?? '*** ERROR ***',
        tagLine: mix.tagLine,
        backgroundImage: this.getMixImageUrl(mix, false),
        thumbnailImage: this.getMixImageUrl(mix, true),
        contentId: mix.uid,
        playable: true,
      };
      if (thirdLine) {
        if (thirdLine === 'd') {
          mi.thirdLine = mix.description;
        } else if (thirdLine === 'h') {
          mi.thirdLine = mix.hostName ?? mix._host?.name;
        }
      }

      return mi;
    }
    return undefined;
  }

  /**
   * Returns the image URL for the given Timeline Segment.
   * @param mix Mix to return image for
   * @param thumbnail
   */
  public getMixImageUrl(mix: PlayerMix, thumbnail = false) {
    let imageUrl: nString;
    let thumbnailUrl: nString;
    if (mix) {
      thumbnailUrl = mix.branding?.thumbnailImageUrl;
      imageUrl = mix.imageUrl;
      if (!imageUrl) {
        if (mix.sourceType === 'Show' && mix._show) {
          thumbnailUrl = mix._show.branding?.thumbnailImageUrl;
          imageUrl = mix._show.imageUrl;
        } else if (mix.sourceType === 'Host' && mix._host) {
          thumbnailUrl = mix._host.branding?.thumbnailImageUrl;
          imageUrl = mix._host.imageUrl;
        }
      }
      if (!imageUrl && mix._program) {
        thumbnailUrl = mix._program.branding?.thumbnailImageUrl;
        imageUrl = mix._program.imageUrl;
      }
      if (!imageUrl) {
        thumbnailUrl = this.station.defaultAlbumArtThumbnailUrl;
        imageUrl = this.station.defaultAlbumArtUrl;
      }
    }
    if (thumbnail && thumbnailUrl) {
      return thumbnailUrl;
    }
    return imageUrl;
  }

  /**
   * Grabs count number of random items.
   * @param items
   * @param count
   */
  public grabRandomItems(
    items: ContentMenuItem[],
    count: number,
  ): ContentMenuItem[] {
    const chosen: ContentMenuItem[] = [];
    const chosenIndexes = this.randomIndexes(count, items.length);
    for (const i of chosenIndexes) {
      chosen.push(items[i]);
    }
    return chosen;
  }

  protected randomIndexes(needed: number, available: number): number[] {
    const chosen: number[] = [];
    if (available < needed) {
      for (let i = 0; i < available; i++) {
        chosen.push(i);
      }
    } else {
      while (chosen.length < needed) {
        const start = Math.floor(Math.random() * available);

        for (let i = 0; i < available; i++) {
          const id = (start + i) % available;
          if (!chosen.includes(id)) {
            chosen.push(id);
            break;
          }
        }
      }
    }
    return chosen;
  }

  /***** Menu Item Getters *****/

  protected programFilter(
    p: PlayerProgram,
    filter: ProgramFilter | undefined | null,
  ): boolean {
    if (filter) {
      if (filter.minPriority !== undefined) {
        if (p.priority === undefined || p.priority < filter.minPriority) {
          return false;
        }
        if (filter.klass) {
          if (p.klass === undefined || p.klass !== filter.klass) {
            return false;
          }
        }
        if (filter.licensingKlass) {
          if (
            p.licensingKlass === undefined ||
            p.licensingKlass !== filter.licensingKlass
          ) {
            return false;
          }
        }
      }
    }
    return true;
  }

  public programsToMenuItems(
    programs: PlayerProgram[],
    filter?: ProgramFilter | null,
    sortByName = true,
  ): ContentMenuItem[] {
    const canidates: ContentMenuItem[] = [];
    for (const item of programs) {
      if (this.programFilter(item, filter)) {
        const mi = this.fromProgram(item);
        if (mi) {
          canidates.push(mi);
        }
      }
    }
    if (sortByName) {
      canidates.sort((a, b) => a.name.localeCompare(b.name));
    }
    return canidates;
  }

  protected hostFilter(
    p: PlayerHost,
    filter: HostFilter | undefined | null,
  ): boolean {
    if (filter) {
      if (filter.minPriority !== undefined) {
        if (p.priority === undefined || p.priority < filter.minPriority) {
          return false;
        }
      }
    }
    return true;
  }

  public hostsToMenuItems(
    hosts: PlayerHost[],
    filter?: HostFilter | null,
    sortByName = true,
  ): ContentMenuItem[] {
    const canidates: ContentMenuItem[] = [];
    for (const item of hosts) {
      if (this.hostFilter(item, filter)) {
        const mi = this.fromHost(item);
        if (mi) {
          canidates.push(mi);
        }
      }
    }
    if (sortByName) {
      canidates.sort((a, b) => a.name.localeCompare(b.name));
    }
    return canidates;
  }

  protected mixFilter(
    p: PlayerMix,
    filter: MixSearchFilter | undefined | null,
  ): boolean {
    if (filter) {
      if (filter.minPriority !== undefined) {
        if (p.priority === undefined || p.priority < filter.minPriority) {
          return false;
        }
      }
    }
    return true;
  }

  public mixesToMenuItems(
    mixes: PlayerMix[],
    thirdLine: 'h' | 'd' | undefined,
    filter?: MixSearchFilter | null,
    sortByName = true,
  ): ContentMenuItem[] {
    const canidates: ContentMenuItem[] = [];
    for (const item of mixes) {
      if (this.mixFilter(item, filter)) {
        const mi = this.fromMix(item, thirdLine);
        if (mi) {
          canidates.push(mi);
        }
      }
    }
    if (sortByName) {
      canidates.sort((a, b) => a.name.localeCompare(b.name));
    }
    return canidates;
  }

  /***** Two week archive management *****/

  public getArchiveDayFor(
    day: string,
    oldestShow: dayjs.Dayjs,
  ): Observable<ContentMenuItem[]> {
    // Let's check if over an hour has passed, if so, clear out and reload
    // Yes, I know that we only need to clear start/end days, but that's more work...
    const now = dayjs();
    if (
      !this.dailyArchivesUpdatedAt ||
      now.diff(this.dailyArchivesUpdatedAt, 'hours') > 1
    ) {
      this.dailyArchives.clear();
      this.dailyArchivesUpdatedAt = now;
    }

    if (day) {
      if (this.dailyArchives.has(day)) {
        return of(this.dailyArchives.get(day) ?? []);
      }
      return this.contentSearcher
        .fetchMixes({ category: 'broadcast-archive', broadcastedOn: day })
        .pipe(
          map((items) => {
            if (items) {
              const mixes: ContentMenuItem[] = [];
              for (const show of items) {
                if (
                  show.broadcastedAt &&
                  show.broadcastedAt.isAfter(oldestShow)
                ) {
                  const mi = this.fromMix(show, 'd');
                  if (mi) {
                    if (show.hostName) {
                      mi.tagLine += ',  Hosted by ' + show.hostName;
                    }
                    mixes.push(mi);
                  }
                }
              }
              this.dailyArchives.set(day, mixes);
              return mixes;
            }
            return [];
          }),
        );
    }
    return of([]);
  }
}
